import isValid from 'date-fns/isValid';
import isBefore from 'date-fns/isBefore';
import isAfter from 'date-fns/isAfter';
import dateFnsFormat from 'date-fns/format';
import parse from 'date-fns/parse';
import dateFnsToDate from 'date-fns/toDate';
import formatISODuration from 'date-fns/formatISODuration';
import subDays from 'date-fns/subDays';
import { es, enUS } from 'date-fns/locale';
import getYear from 'date-fns/getYear';

const STANDARD_DATE_FORMAT = 'yyyy-MM-dd';
const DATE_FORMATS_BY_LANG = {
    en: {
        locale: enUS,
        formats: {
            long: 'LLLL d, yyyy',
            short: 'MM/dd/yyyy',
            standard: STANDARD_DATE_FORMAT,
        },
    },
    es: {
        locale: es,
        formats: {
            long: "d 'de' LLLL 'de' yyyy",
            short: 'dd/MM/yyyy',
            standard: STANDARD_DATE_FORMAT,
        },
    },
};

class DateUtils {

    /**
     * Returns a parsed date.
     * @param {(Date|String|Number)} value the original date value
     * @returns {(Date|null)} parsed date or null
    */
    static date = (value) => {

        if(typeof value === 'undefined') {

            return new Date();

        }

        if (value === null) {

            return null;

        }

        return new Date(value);

    }

    /**
     * Is the given date valid?
     * @param {*} date the date to check
     * @returns {Boolean} the date is valid
    */
    static isValid = (date) => isValid(date);

    /**
     * Is the first date before the second one?
     * @param {(Date | Number)} date the date that should be before the other one to return true
     * @param {(Date | Number)} dateToCompare the date to compare with
     * @returns {Boolean} the first date is before the second date
    */
    static isBefore = (date, dateToCompare) => isBefore(date, dateToCompare);

    /**
     * Is the first date after the second one?
     * @param {(Date | Number)} date the date that should be after the other one to return true
     * @param {(Date | Number)} dateToCompare the date to compare with
     * @returns {Boolean} the first date is after the second date
    */
    static isAfter = (date, dateToCompare) => isAfter(date, dateToCompare);

    /**
     * Returns a formatted date.
     * @param {(Date|String|Number)} date the original date
     * @param {('en'|'es')} lang language acronym used to localize output format
     * @param {('long'|'short')} format format modifier
     * @returns {String} formatted date string or empty string
    */
    static formatDate = (date, lang = 'en', format = 'long') => {

        const parsedDate = this.date(date);

        if(!this.isValid(parsedDate)) {

            return '';

        }

        const formattedLang = lang.toLowerCase();
        const utcMilliseconds = this.dateToUtcMilliseconds(parsedDate);
        return dateFnsFormat(utcMilliseconds, DATE_FORMATS_BY_LANG[formattedLang].formats[format], { locale: DATE_FORMATS_BY_LANG[formattedLang].locale });

    }

    /**
     * Returns utc date milliseconds.
     * @param {(Date|String|Number)} date the original date
     * @returns {(Number|null)} utc date milliseconds number or null
    */
    static dateToUtcMilliseconds = (date) => {

        const parsedDate = this.date(date);

        if(!this.isValid(parsedDate)) {

            return null;

        }

        return parsedDate.getTime() + (parsedDate.getTimezoneOffset() * 60 * 1000);

    }

    /**
     * Returns a parsed timestamp date.
     * @param {String} timestamp the original timestamp
     * @param {String} format format modifier
     * @returns {Date} parsed timestamp date
    */
    static parseTimestampToDate = (timestamp, format = STANDARD_DATE_FORMAT) => {

        return dateFnsToDate(parse(timestamp, format, new Date()));

    }

    /**
     * Returns a duration iso string.
     * @param {String} duration the original duration
     * @returns {(String|null)} duration iso string or null
    */
    static durationToISOString = (duration) => {

        if(!duration) {

            return null;

        }

        let parsedTime = parse(duration, 'HH:mm:ss', new Date());

        const durationObj = {
            hours: parsedTime.getHours(),
            minutes: parsedTime.getMinutes(),
            seconds: parsedTime.getSeconds(),
        };

        return formatISODuration(durationObj);

    }

    /**
     * Returns a formatted time.
     * @param {String} time the original time
     * @returns {String} formatted time string or empty string
    */
    static formatTime = (time) => {

        // If null or a number passed in, return empty string.
        if (typeof time !== 'string') {

            return '';

        }

        // If another string in, not in hh:mm:ss format, just return it.
        if (!time.match(/^\d{2}:\d{2}:\d{2}$/)) {

            return time;

        }
        return time.split(':').reduce((acc, currVal, currIndex) => {

            let newVal = currVal;
            if (currIndex === 0) {

                if (currVal === '00') return '';
                return `${parseInt(currVal)}:`;

            }
            if (currIndex === 1) {

                if (acc !== '') {

                    newVal = `${currVal}:`;

                } else {

                    newVal = `${parseInt(currVal)}:`;

                }

            }
            return acc + newVal;

        }, '');

    }


    /**
     * Returns a formatted time.
     *
     * @param {String} seconds the original seconds
     * @returns {String} formatted time string
    */
    static formatSecondsToTime = (seconds) => {

        const time = this.date(seconds * 1000)
            .toISOString()
            .substr(11, 8);

        return this.formatTime(time);

    }

    /**
     * Returns time in seconds.
     *
     * @param {String} duration the duration
     * @returns {Number} time in seconds
     * @desc duration string formats
     * '.SSS'
     * 'SS'
     * 'MM:SS'
     * 'HH:MM:SS'
     * 'HH:MM:SS.SSS'
    */
    static formatDurationToSeconds = (duration = '') => {

        return duration.split(':').reduce((acc, time) => isNaN(time) ? 0 : (60 * acc) + +time, 0) || 0;

    }

    /**
     * Subtract the specified number of days from the given date.
     *
     * @param {(Date|Number)} date the date to be changed.s
     * @param {Number} amount the amount of days to be subtracted.
     * @returns {Date} the new date with the days subtracted
     */
    static subDays = (date, amount) => subDays(date, amount)

    /**
     * Get the current year from date.
     *
     * @param {(Date|Number)} date the date to check
     * @returns {Number} the Year
     */
     static getYear = (date) => getYear(date);

}

export default DateUtils;
